import { db } from "../firebaseClient"
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore"
import { awardPoints } from "./learnerPointsHelpers"
import { logActivity } from "./activityHelpers"
import { logLearningEvent } from "./learningEventHelpers"
import { dispatchWebhook } from "./webhookHelpers"
import { logPlatformEvent } from "./eventHelpers"
// Enrollment docs use a deterministic ID (userId_courseId) instead of an auto ID.
// This makes "is this user already enrolled in this course" a single getDoc instead
// of a query, and makes double-enrollment structurally impossible (same ID = same doc).
export const enrollmentDocId = (userId, courseId) => `${userId}_${courseId}`

// Reads a learner's identity fields off their users/{uid} doc, for denormalizing
// onto a new enrollment (see createEnrollment below) so learner-facing tables
// never need a per-row lookup. Falls back to blanks for accounts that predate
// the users/{uid} doc (or existing enrollments created before this change).
export const getLearnerIdentity = async (userId) => {
  try {
    const snap = await getDoc(doc(db, "users", userId))
    if (!snap.exists()) return { name: "", email: "", avatarUrl: "", nesolearnId: "" }
    const data = snap.data()
    return {
      name: data?.name || "",
      email: data?.email || "",
      avatarUrl: data?.avatarUrl || "",
      nesolearnId: data?.nesolearnId || "",
    }
  } catch (error) {
    return { name: "", email: "", avatarUrl: "", nesolearnId: "" }
  }
}

// Creates an enrollment for a learner in a course. Safe to call even if an enrollment
// already exists — it won't be overwritten (checked first) so re-clicking Enroll Now
// can't reset progress.
export const createEnrollment = async ({
  userId,
  courseId,
  orgId,
  enrollmentType,
  lessonsTotal,
  cohortName,
  learnerName,
  learnerEmail,
  learnerAvatarUrl,
  learnerNesolearnId,
}) => {
  if (!userId || !courseId || !orgId) {
    return { enrollment: null, error: { message: "Missing required enrollment fields" } }
  }

  try {
    const enrollmentId = enrollmentDocId(userId, courseId)
    const enrollmentRef = doc(db, "enrollments", enrollmentId)

    const existing = await getDoc(enrollmentRef)
    if (existing.exists()) {
      return { enrollment: { enrollmentId, ...existing.data() }, error: null }
    }

    // Learner identity is denormalized here at creation time — there's no
    // separate learners collection, so this is what the Learners table/360
    // profile reads from directly. Callers that don't pass these yet just
    // get blanks stored, handled with a fallback on the display side.
    const enrollmentData = {
      enrollmentId,
      courseId,
      orgId,
      userId,
      enrollmentMethod: enrollmentType || "open",
      cohortName: cohortName || "",
      learnerName: learnerName || "",
      learnerEmail: learnerEmail || "",
      learnerAvatarUrl: learnerAvatarUrl || "",
      learnerNesolearnId: learnerNesolearnId || "",
      enrolledAt: serverTimestamp(),
      progressPercent: 0,
      lessonsCompleted: 0,
      completedLessonIds: [],
      lessonsTotal: lessonsTotal || 0,
      moduleQuizResults: {},
      capstoneResult: null,
      pointsEarned: 0,
      lastActivityAt: serverTimestamp(),
      status: "active",
    }

        await setDoc(enrollmentRef, enrollmentData)
    logPlatformEvent({
      type: "ENROLLMENT_CREATED",
      actorUid: userId,
      orgId,
      targetPath: `enrollments/${enrollmentId}`,
      metadata: { courseId, enrollmentType: enrollmentType || "open" },
    })
    return { enrollment: enrollmentData, error: null }
  } catch (error) {
    return { enrollment: null, error }
  }
}

// Checks whether a learner is already enrolled in a specific course
export const getUserEnrollment = async (userId, courseId) => {
  if (!userId || !courseId) return { enrollment: null, error: null }

  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentDocId(userId, courseId))
    const snap = await getDoc(enrollmentRef)
    if (!snap.exists()) return { enrollment: null, error: null }
    return { enrollment: snap.data(), error: null }
  } catch (error) {
    return { enrollment: null, error }
  }
}

// Marks a lesson complete/incomplete for a learner and recomputes progress.
// Reads-then-writes rather than using arrayUnion/increment so progressPercent
// stays derived and always consistent with completedLessonIds, even if
// lessonsTotal has changed since enrollment (e.g. org added more lessons).
export const toggleLessonCompletion = async ({ userId, courseId, lessonId, lessonsTotal, markComplete, courseTitle }) => {
  if (!userId || !courseId || !lessonId) {
    return { enrollment: null, error: { message: "Missing required fields" } }
  }

  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentDocId(userId, courseId))
    const snap = await getDoc(enrollmentRef)
    if (!snap.exists()) {
      return { enrollment: null, error: { message: "No enrollment found" } }
    }

    const current = snap.data()
    const currentCompleted = current?.completedLessonIds || []

        const nextCompleted = markComplete
      ? Array.from(new Set([...currentCompleted, lessonId]))
      : currentCompleted?.filter((id) => id !== lessonId)

    const isNewCompletion = markComplete && !currentCompleted?.includes(lessonId)

    const total = lessonsTotal || current?.lessonsTotal || 0
    const progressPercent = total > 0 ? Math.round((nextCompleted?.length / total) * 100) : 0
    const justCompletedCourse = progressPercent >= 100 && (current?.progressPercent || 0) < 100

        const updates = {
      completedLessonIds: nextCompleted,
      lessonsCompleted: nextCompleted?.length,
      lessonsTotal: total,
      progressPercent,
      lastActivityAt: serverTimestamp(),
      ...(justCompletedCourse ? { completedAt: serverTimestamp() } : {}),
    }

        await updateDoc(enrollmentRef, updates)

    if (isNewCompletion) {
      logLearningEvent({ orgId: current?.orgId, userId, courseId, type: "lesson_completed" }).catch(() => {})
    }

                if (justCompletedCourse) {
      logActivity(userId, {
        type: "course_completed",
        label: `Completed ${courseTitle || "a course"}`,
        icon: "GraduationCap",
        color: "text-success",
      }).catch(() => {})
      dispatchWebhook(current?.orgId, "course.completed", { userId, courseId, courseTitle }).catch(() => {})
      logPlatformEvent({
        type: "COURSE_COMPLETED",
        actorUid: userId,
        orgId: current?.orgId,
        targetPath: `enrollments/${enrollmentDocId(userId, courseId)}`,
        metadata: { courseId, courseTitle: courseTitle || "" },
      })
    }
    return { enrollment: { ...current, ...updates }, error: null }
  } catch (error) {
    return { enrollment: null, error }
  }
}

// Touches lastActivityAt without changing completion — called when a learner opens a lesson.
// Also opportunistically backfills learnerEmail/learnerAvatarUrl on enrollments
// created before identity was denormalized, using whatever users/{uid} doc
// exists by now (see authHelpers.signIn/signInWithGoogle self-heal). Only
// fires the extra read once per enrollment — after the first successful
// backfill learnerEmail is no longer blank and this check short-circuits.
export const touchEnrollmentActivity = async (userId, courseId) => {
  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentDocId(userId, courseId))
    const updates = { lastActivityAt: serverTimestamp() }

    const snap = await getDoc(enrollmentRef)
    if (snap.exists() && !snap.data()?.learnerEmail) {
      const identity = await getLearnerIdentity(userId)
      if (identity.email) updates.learnerEmail = identity.email
      if (identity.avatarUrl) updates.learnerAvatarUrl = identity.avatarUrl
      if (identity.nesolearnId) updates.learnerNesolearnId = identity.nesolearnId
    }

    await updateDoc(enrollmentRef, updates)
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Auto-grades a set of answers against a quiz/capstone's questions.
// answers: { [questionId]: selectedOptionId }
// NOTE: this runs client-side against data already fetched in the course doc (public
// read), so correct answers are technically visible in devtools before submission.
// Real anti-cheat needs server-side grading via Cloud Functions — deferred, see notes.
export const gradeQuiz = (questions, answers) => {
  const total = questions?.length || 0
  if (total === 0) return { score: 0, correctCount: 0, total: 0 }

  const correctCount = questions?.reduce((count, q) => {
    return answers?.[q?.id] === q?.correctOptionId ? count + 1 : count
  }, 0)

  return { score: Math.round((correctCount / total) * 100), correctCount, total }
}

// Submits a module quiz attempt. Policy: ONE locked, graded attempt during the course.
// A single retake is permitted only once the learner has finished every lesson in the
// course (areAllLessonsComplete) — caller passes allowRetake to signal this. Stores the
// learner's actual answers so they can review right/wrong afterward, with resourceHint
// shown on missed questions.
export const submitModuleQuiz = async ({ userId, courseId, moduleId, quiz, answers, allowRetake }) => {
  if (!userId || !courseId || !moduleId || !quiz) {
    return { result: null, error: { message: "Missing required fields" } }
  }

  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentDocId(userId, courseId))
    const snap = await getDoc(enrollmentRef)
    if (!snap.exists()) return { result: null, error: { message: "No enrollment found" } }

    const current = snap.data()
    const existingResult = current?.moduleQuizResults?.[moduleId]
    const attemptCount = existingResult?.attemptCount || 0

    if (attemptCount >= 1 && !allowRetake) {
      return {
        result: null,
        error: { message: "This quiz has already been attempted. Retakes unlock once you complete the course." },
      }
    }
    if (attemptCount >= 2) {
      return { result: null, error: { message: "No further retakes are available for this quiz." } }
    }

    const { score } = gradeQuiz(quiz?.questions, answers)
    const passed = score >= (quiz?.passingScore || 70)

    const alreadyAwarded = !!existingResult?.pointsAwarded
    const bestScore = Math.max(score, existingResult?.bestScore || 0)

    const moduleQuizResults = {
      ...(current?.moduleQuizResults || {}),
      [moduleId]: {
        score,
        bestScore,
        passed,
        answers,
        attemptCount: attemptCount + 1,
        pointsAwarded: alreadyAwarded || passed,
        lastAttemptAt: serverTimestamp(),
      },
    }

    const pointsToAward = passed && !alreadyAwarded ? quiz?.pointsReward || 0 : 0
    const updates = {
      moduleQuizResults,
      pointsEarned: (current?.pointsEarned || 0) + pointsToAward,
      lastActivityAt: serverTimestamp(),
    }

        await updateDoc(enrollmentRef, updates)
    if (pointsToAward > 0) awardPoints(userId, pointsToAward).catch(() => {})
    logLearningEvent({ orgId: current?.orgId, userId, courseId, type: "assessment_completed" }).catch(() => {})

    return { result: { score, passed, pointsAwarded: pointsToAward, answers }, error: null }
  } catch (error) {
    return { result: null, error }
  }
}

// Capstone unlocks once every lesson is complete AND the learner's average score across
// all module quizzes attempted is >= 60% (cumulative policy — replaces the earlier
// per-quiz-must-individually-pass rule). A module quiz that hasn't been attempted yet
// counts as 0 in the average, so skipping quizzes still blocks the capstone.
export const CUMULATIVE_QUIZ_UNLOCK_THRESHOLD = 60

export const getCumulativeQuizAverage = (course, enrollment) => {
  const modulesWithQuizzes = course?.modules?.filter((m) => !!m?.quiz) || []
  if (modulesWithQuizzes?.length === 0) return 100

  const total = modulesWithQuizzes?.reduce((sum, m) => {
    const score = enrollment?.moduleQuizResults?.[m?.id]?.bestScore || 0
    return sum + score
  }, 0)

  return Math.round(total / modulesWithQuizzes?.length)
}

export const isCapstoneUnlocked = (course, enrollment) => {
  const flatLessons = []
  course?.modules?.forEach((m) => m?.lessons?.forEach((l) => flatLessons.push(l)))

  const completedLessonIds = enrollment?.completedLessonIds || []
  const allLessonsDone = flatLessons?.every((l) => completedLessonIds?.includes(l?.id))

  const cumulativeAverage = getCumulativeQuizAverage(course, enrollment)

  return !!allLessonsDone && cumulativeAverage >= CUMULATIVE_QUIZ_UNLOCK_THRESHOLD
}

// True once every lesson in the course is marked complete. Gates the single-retake
// window for quizzes/capstone — a retake only unlocks after the learner has finished
// the WHOLE course, not just the module the quiz belongs to (matches academic policy:
// one graded attempt during the course, review + one retake after completion).
export const areAllLessonsComplete = (course, enrollment) => {
  const flatLessons = []
  course?.modules?.forEach((m) => m?.lessons?.forEach((l) => flatLessons.push(l)))
  if (flatLessons?.length === 0) return false

  const completedLessonIds = enrollment?.completedLessonIds || []
  return flatLessons?.every((l) => completedLessonIds?.includes(l?.id))
}

// Submits a capstone attempt. Same locked-attempt policy as submitModuleQuiz. On pass,
// sets certifiedEligible if the course has certification enabled + requires assessment.
// Note: by the time a learner can even attempt the capstone (isCapstoneUnlocked), the
// course is already fully complete — so allowRetake will typically already be true
// right after the first attempt. That's expected, not a bug: the course-completion
// prerequisite to unlock the capstone at all already satisfies the retake condition.
export const submitCapstone = async ({ userId, courseId, capstone, answers, certification, allowRetake }) => {
  if (!userId || !courseId || !capstone) {
    return { result: null, error: { message: "Missing required fields" } }
  }

  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentDocId(userId, courseId))
    const snap = await getDoc(enrollmentRef)
    if (!snap.exists()) return { result: null, error: { message: "No enrollment found" } }

    const current = snap.data()
    const attemptCount = current?.capstoneResult?.attemptCount || 0

    if (attemptCount >= 1 && !allowRetake) {
      return {
        result: null,
        error: { message: "The capstone has already been attempted. Retakes unlock once you complete the course." },
      }
    }
    if (attemptCount >= 2) {
      return { result: null, error: { message: "No further retakes are available for the capstone." } }
    }

    const { score } = gradeQuiz(capstone?.questions, answers)
    const passed = score >= (capstone?.passingScore || 80)

    const alreadyAwarded = !!current?.capstoneResult?.pointsAwarded
    const certifiedEligible =
      passed &&
      !!certification?.enabled &&
      !!certification?.requiresAssessment &&
      score >= (certification?.passingScore || 80)

    const capstoneResult = {
      score,
      bestScore: Math.max(score, current?.capstoneResult?.bestScore || 0),
      passed,
      answers,
      attemptCount: attemptCount + 1,
      pointsAwarded: alreadyAwarded || passed,
      certifiedEligible: certifiedEligible || !!current?.capstoneResult?.certifiedEligible,
      lastAttemptAt: serverTimestamp(),
    }

    const pointsToAward = passed && !alreadyAwarded ? capstone?.pointsReward || 0 : 0
    const updates = {
      capstoneResult,
      pointsEarned: (current?.pointsEarned || 0) + pointsToAward,
      lastActivityAt: serverTimestamp(),
    }

        await updateDoc(enrollmentRef, updates)
    if (pointsToAward > 0) awardPoints(userId, pointsToAward).catch(() => {})
    logLearningEvent({ orgId: current?.orgId, userId, courseId, type: "assessment_completed" }).catch(() => {})

    return { result: { score, passed, certifiedEligible, pointsAwarded: pointsToAward, answers }, error: null }
  } catch (error) {
    return { result: null, error }
  }
}

// Records that a certificate has been issued for this enrollment, so re-passing
// the capstone (retakes) never issues a duplicate certificate.
export const markEnrollmentCertificateIssued = async (userId, courseId, certId) => {
  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentDocId(userId, courseId))
    await updateDoc(enrollmentRef, { certificateId: certId })
    return { error: null }
  } catch (error) {
    return { error }
  }
}
// Enrollments for one specific course — used by the Cohorts panel to show
// which learners are enrollable into a cohort for that course.
export const listCourseEnrollments = async (orgId, courseId) => {
  try {
    const enrollmentsRef = collection(db, "enrollments")
    const q = query(enrollmentsRef, where("orgId", "==", orgId), where("courseId", "==", courseId))
    const snap = await getDocs(q)
    const enrollments = snap.docs?.map((d) => d.data())
    return { enrollments, error: null }
  } catch (error) {
    return { enrollments: [], error }
  }
}

// Assigns a learner to a real cohort doc on their existing enrollment. Org
// admins call this from the Cohorts workspace. Writes both cohortId (for
// lookups by the new cohort workspace) and cohortName (kept in sync so the
// existing cohortPerformanceHelpers.js / getProgramCohorts groupings, which
// group by name, keep working unmodified).
export const assignCohort = async (userId, courseId, { cohortId, cohortName }) => {
  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentDocId(userId, courseId))
    await updateDoc(enrollmentRef, { cohortId: cohortId || "", cohortName: cohortName || "" })
    return { error: null }
  } catch (error) {
    return { error }
  }
}
// Lists all enrollments for an org, across all courses/learners — the source of
// truth for "Total Learners" (distinct userIds) and "Active Learners" on the
// dashboard KPI row, since there's no separate learners collection.
export const listOrgEnrollments = async (orgId) => {
  try {
    const enrollmentsRef = collection(db, "enrollments")
    const q = query(enrollmentsRef, where("orgId", "==", orgId))
    const snap = await getDocs(q)
    const enrollments = snap.docs?.map((d) => d.data())
    return { enrollments, error: null }
  } catch (error) {
    return { enrollments: [], error }
  }
}
// Lists all of a learner's enrollments (for a future "my courses" view)
export const listUserEnrollments = async (userId) => {
  try {
    const enrollmentsRef = collection(db, "enrollments")
    const q = query(enrollmentsRef, where("userId", "==", userId))
    const snap = await getDocs(q)
    const enrollments = snap.docs?.map((d) => d.data())
    return { enrollments, error: null }
  } catch (error) {
    return { enrollments: [], error }
  }
}